import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Heart, ShoppingBag, Star } from "lucide-react";
import { api, formatPrice, imgUrl } from "../lib/api";
import { useCart } from "../lib/CartContext";
import { useAuth, loginWithGoogle } from "../lib/AuthContext";
import ProductCard from "../components/ProductCard";
import WhatsAppWidget from "../components/WhatsAppWidget";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const { add, toggleWishlist, isWishlisted } = useCart();
  const { user } = useAuth();
  const [revForm, setRevForm] = useState({ rating: 5, comment: "" });

  useEffect(() => {
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data);
      api.get("/products", { params: { category: r.data.category, limit: 4 } }).then(rr => {
        setRelated(rr.data.filter(p => p.id !== id).slice(0, 3));
      });
    }).catch(() => {});
    api.get(`/reviews/${id}`).then(r => setReviews(r.data));
    setActiveImg(0);
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { loginWithGoogle(); return; }
    try {
      await api.post("/reviews", { product_id: id, rating: revForm.rating, comment: revForm.comment });
      toast.success("Recenzie publicată");
      setRevForm({ rating: 5, comment: "" });
      const r = await api.get(`/reviews/${id}`);
      setReviews(r.data);
    } catch { toast.error("Eroare la trimitere"); }
  };

  if (!product) return <main className="pt-40 text-center text-[#A39E8A]">Se încarcă...</main>;
  const wished = isWishlisted(product.id);
  const images = product.images?.length ? product.images.map(imgUrl) : ["https://images.unsplash.com/photo-1627899590758-9a41d43d49e6?q=85"];

  return (
    <main className="pt-28 pb-20" data-testid="product-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <p className="label-luxe text-[#A39E8A] mb-4" data-testid="breadcrumb">
          <a href="/shop" className="hover:text-[#D4AF37]">Magazin</a> / <a href={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-[#D4AF37]">{product.category}</a>
        </p>
        <div className="grid lg:grid-cols-2 gap-16">
          {/* IMAGES */}
          <div>
            <div
              className="relative aspect-[4/5] overflow-hidden bg-[#0F0F0F] cursor-zoom-in"
              onMouseEnter={() => setZoom(z => ({ ...z, active: true }))}
              onMouseLeave={() => setZoom(z => ({ ...z, active: false }))}
              onMouseMove={e => {
                const r = e.currentTarget.getBoundingClientRect();
                setZoom({ active: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
              }}
              data-testid="product-main-image"
            >
              <img
                src={images[activeImg]} alt={product.name}
                style={zoom.active ? { transform: "scale(1.8)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}
                className="w-full h-full object-cover transition-transform duration-300"
              />
              {product.rare && (
                <span className="absolute top-4 left-4 label-luxe bg-black/70 text-[#D4AF37] px-3 py-2 backdrop-blur-sm border border-[#D4AF37]/40">Piesă Rară</span>
              )}
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-3 mt-4">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden border ${i === activeImg ? "border-[#D4AF37]" : "border-[#D4AF37]/15"}`} data-testid={`thumb-${i}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* DETAILS */}
          <div>
            <p className="label-luxe text-[#D4AF37] mb-3">{product.category}</p>
            <h1 className="h-display text-4xl sm:text-5xl text-[#F8F4E6] mb-6">{product.name}</h1>
            {product.rating > 0 && (
              <div className="flex items-center gap-2 mb-6">
                <div className="flex gap-1 text-[#D4AF37]">
                  {[1,2,3,4,5].map(s => <Star key={s} size={14} fill={s <= Math.round(product.rating) ? "currentColor" : "none"} strokeWidth={1} />)}
                </div>
                <span className="text-xs text-[#A39E8A] tracking-widest">{product.rating.toFixed(1)} · {product.reviews_count} recenzii</span>
              </div>
            )}
            <p className="font-serif text-4xl text-[#D4AF37] mb-8" data-testid="product-price">{formatPrice(product.price, product.currency)}</p>
            <p className="text-[#A39E8A] leading-relaxed mb-10">{product.description}</p>

            <div className="flex flex-col sm:flex-row gap-3 mb-12">
              <button onClick={() => add(product.id, 1)} className="btn-gold flex-1 justify-center" data-testid="add-to-cart-btn"><ShoppingBag size={14} /> Adaugă în Coș</button>
              <button onClick={() => toggleWishlist(product.id)} className={`btn-ghost ${wished ? "bg-[#D4AF37]/10" : ""}`} data-testid="wishlist-btn">
                <Heart size={14} fill={wished ? "#D4AF37" : "none"} /> {wished ? "În Listă" : "La Listă"}
              </button>
            </div>

            <dl className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm border-t border-[#D4AF37]/15 pt-8" data-testid="product-spec">
              {[
                ["Origine", product.origin],
                ["Materiale", product.materials],
                ["Dimensiuni", product.dimensions],
                ["Stare", product.condition],
              ].map(([k, v]) => v && (
                <div key={k}>
                  <dt className="label-luxe text-[#A39E8A] mb-1">{k}</dt>
                  <dd className="text-[#F8F4E6]">{v}</dd>
                </div>
              ))}
            </dl>

            {product.history && (
              <div className="mt-10 pt-8 border-t border-[#D4AF37]/15">
                <p className="label-luxe text-[#D4AF37] mb-3">Istorie & Proveniență</p>
                <p className="text-[#A39E8A] leading-relaxed font-serif italic text-lg">{product.history}</p>
              </div>
            )}
          </div>
        </div>

        {/* REVIEWS */}
        <section className="mt-24" data-testid="reviews-section">
          <h2 className="h-display text-3xl sm:text-4xl text-[#F8F4E6] mb-10">Recenzii Clienți</h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <form onSubmit={submitReview} className="space-y-6 border border-[#D4AF37]/15 p-8" data-testid="review-form">
              <p className="label-luxe text-[#D4AF37]">Lasă o Recenzie</p>
              <div>
                <p className="label-luxe mb-2">Notare</p>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(s => (
                    <button type="button" key={s} onClick={() => setRevForm(f => ({ ...f, rating: s }))} className="text-[#D4AF37]" data-testid={`star-${s}`}>
                      <Star size={22} fill={s <= revForm.rating ? "currentColor" : "none"} strokeWidth={1.2} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea value={revForm.comment} onChange={e => setRevForm(f => ({ ...f, comment: e.target.value }))} placeholder="Comentariul tău..." required className="input-luxe" data-testid="review-comment" />
              <button type="submit" className="btn-gold" data-testid="submit-review">Publică Recenzia</button>
            </form>
            <div className="space-y-6">
              {reviews.length === 0 ? (
                <p className="text-[#A39E8A] italic">Fii primul care recenzează această piesă.</p>
              ) : reviews.map(r => (
                <article key={r.id} className="border-b border-[#D4AF37]/10 pb-6">
                  <div className="flex gap-1 text-[#D4AF37] mb-2">
                    {[1,2,3,4,5].map(s => <Star key={s} size={12} fill={s <= r.rating ? "currentColor" : "none"} strokeWidth={1} />)}
                  </div>
                  <p className="text-[#F8F4E6] font-serif text-lg italic mb-2">"{r.comment}"</p>
                  <p className="label-luxe text-[#A39E8A]">{r.user_name}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* RELATED */}
        {related.length > 0 && (
          <section className="mt-24">
            <h2 className="h-display text-3xl sm:text-4xl text-[#F8F4E6] mb-10">Piese Complementare</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
            </div>
          </section>
        )}
      </div>
      <WhatsAppWidget productId={product.id} />
    </main>
  );
}
